/*
 * Luminaire lead tracking -> dataLayer
 * ------------------------------------
 * Fires a `form_submit_website` dataLayer event on EVERY successful lead-form
 * submission, with a HARDCODED `event_action` for the known form types and a
 * derived (slugified) action for any other form.
 *
 * Most lead forms are Globo Powerful Form Builder forms. Globo dispatches a
 * document-level `globo.formbuilder.form.submitted` event only inside its own
 * success branch (response.success === "true"), so that is the source of truth
 * for a completed Globo submission -- no XHR interception or DOM polling.
 *
 * event_action is resolved by a HYBRID key: mostly by Globo form id, but form
 * 143281 (Showroom Appointment) is reused across three landing pages that each
 * need a different action, so it is disambiguated by page handle.
 *
 * The Design Concierge form (/pages/design-concierge) is NOT a Globo form -- it
 * is a custom questionnaire in sections/design-concierge.liquid that posts to a
 * webhook. That section calls window.LuminaireLeadEvents.fire(...) directly.
 *
 * event_action map
 *   Globo 139552 (Contact us)          -> contact-form-submit
 *   Globo 141848 (Product Inquiry)     -> generate_lead_product_inquiry
 *   Globo 143281 (Showroom Appointment):
 *       page handle lp-design-concierge      -> design_concierge_lp
 *       page handle lp-schedule-appointment  -> showroom_appointment_lp
 *       otherwise (e.g. showroom-appointment) -> generate_lead_appointment
 *   Custom Design Concierge form       -> form_design_concierge  (fired by the section)
 *   Any other Globo form               -> slug of its Globo form name
 *                                         (e.g. "Careers - V2" -> "careers")
 *
 * Emitted event shape:
 *   dataLayer.push({
 *     event: "form_submit_website",
 *     eventModel: { event_category: "form-submit", event_action: "contact-form-submit" },
 *     user_data: { email: "test@test.it", sha256_email: "ac6dcb..." },
 *     form_id: "139552",
 *     form_name: "Contact us - V2",
 *     submission_id: "12345"   // when available
 *   });
 */
(function () {
  'use strict';

  if (window.__globoLeadEventsBound) return;
  window.__globoLeadEventsBound = true;

  // Flip to true to trace resolution/pushes in the console during QA.
  var DEBUG = false;

  window.dataLayer = window.dataLayer || [];

  // --- config ----------------------------------------------------------------

  var EVENT_NAME = 'form_submit_website';

  // Globo form id -> fixed event_action (forms that map 1:1).
  var FORM_ACTION = {
    '139552': 'contact-form-submit',           // Contact us - V2
    '141848': 'generate_lead_product_inquiry'  // Product Inquiry - V2 (global drawer)
  };

  // Form 143281 (Showroom Appointment) is the same form on several landing
  // pages; page handle decides the action. Default is the plain appointment.
  var APPOINTMENT_FORM_ID = '143281';
  var APPOINTMENT_BY_HANDLE = {
    'lp-design-concierge': 'design_concierge_lp',
    'lp-schedule-appointment': 'showroom_appointment_lp'
  };
  var APPOINTMENT_DEFAULT = 'generate_lead_appointment';

  // --- helpers ---------------------------------------------------------------

  function log() {
    if (DEBUG && window.console) console.log.apply(console, ['[lead-events]'].concat([].slice.call(arguments)));
  }

  function normalizeEmail(value) {
    return (value || '').toString().trim().toLowerCase();
  }

  // Fallback action for forms with no hardcoded mapping: slug of the form name,
  // dropping a trailing version marker like "- V2". Empty name -> globo-<id>.
  function slugify(value) {
    return (value || '')
      .toString()
      .toLowerCase()
      .replace(/[\s_-]*v\d+\s*$/i, '')
      .trim()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Last non-empty path segment, ignoring a leading locale prefix (/it, /fr...).
  function pageHandle() {
    var parts = (window.location.pathname || '').split('/').filter(Boolean);
    return parts.length ? parts[parts.length - 1].toLowerCase() : '';
  }

  // Resolve the event_action for a Globo form. Known forms use their hardcoded
  // action; form 143281 is split by page handle; anything else falls back to a
  // slug of the Globo form name so every form still fires a tracked event.
  function resolveGloboAction(formId, formName) {
    formId = String(formId || '');
    if (formId === APPOINTMENT_FORM_ID) {
      return APPOINTMENT_BY_HANDLE[pageHandle()] || APPOINTMENT_DEFAULT;
    }
    return FORM_ACTION[formId] || slugify(formName) || ('globo-' + formId);
  }

  // Pull the submitter's email out of a Globo form node. Globo names the primary
  // email field "email-1" (first field whose name starts with "email"); logged
  // in customers also get a hidden "customer[email]" mirror as a fallback.
  function getGloboEmail(form) {
    if (!form) return '';
    var fields = form.querySelectorAll('input, textarea, select');
    for (var i = 0; i < fields.length; i++) {
      var name = fields[i].getAttribute('name') || '';
      if (/^email/i.test(name) && fields[i].value) return fields[i].value;
    }
    var customer = form.querySelector('input[name="customer[email]"]');
    return customer && customer.value ? customer.value : '';
  }

  function getGloboFormName(formId, form) {
    try {
      var f = window.Globo &&
        window.Globo.FormBuilder &&
        window.Globo.FormBuilder.forms &&
        window.Globo.FormBuilder.forms[formId];
      if (f && f.title) return f.title;
    } catch (e) {}
    return form ? (form.getAttribute('aria-label') || '') : '';
  }

  function getSubmissionId(detail) {
    var res = detail && detail.response;
    if (!Array.isArray(res)) return '';
    for (var i = 0; i < res.length; i++) {
      if (res[i] && String(res[i].success) === 'true' && res[i].id != null) {
        return String(res[i].id);
      }
    }
    return '';
  }

  // SHA-256 -> lowercase hex. Prefers SubtleCrypto (available on https)
  function sha256Hex(message) {
    if (window.crypto && window.crypto.subtle && window.TextEncoder) {
      try {
        var data = new TextEncoder().encode(message);
        return window.crypto.subtle.digest('SHA-256', data).then(function (buf) {
          var bytes = new Uint8Array(buf);
          var hex = '';
          for (var i = 0; i < bytes.length; i++) {
            hex += bytes[i].toString(16).padStart(2, '0');
          }
          return hex;
        });
      } catch (e) {
        return Promise.resolve(sha256Fallback(message));
      }
    }
    return Promise.resolve(sha256Fallback(message));
  }

  function sha256Fallback(ascii) {
    function rrot(n, x) { return (x >>> n) | (x << (32 - n)); }
    var mathPow = Math.pow, maxWord = mathPow(2, 32), result = '';
    var words = [], asciiBitLength;

    var utf8 = unescape(encodeURIComponent(ascii));
    asciiBitLength = utf8.length * 8;

    var hash = sha256Fallback.h = sha256Fallback.h || [];
    var k = sha256Fallback.k = sha256Fallback.k || [];
    var primeCounter = k.length;
    var isComposite = {};
    for (var candidate = 2; primeCounter < 64; candidate++) {
      if (!isComposite[candidate]) {
        for (var i = 0; i < 313; i += candidate) isComposite[i] = candidate;
        hash[primeCounter] = (mathPow(candidate, 0.5) * maxWord) | 0;
        k[primeCounter++] = (mathPow(candidate, 1 / 3) * maxWord) | 0;
      }
    }

    hash = hash.slice(0, 8);
    var h = hash.slice(0);

    utf8 += '\x80';
    while (utf8.length % 64 - 56) utf8 += '\x00';
    for (i = 0; i < utf8.length; i++) {
      var j = utf8.charCodeAt(i);
      if (j >> 8) return '';
      words[i >> 2] |= j << ((3 - i) % 4) * 8;
    }
    words[words.length] = (asciiBitLength / maxWord) | 0;
    words[words.length] = asciiBitLength;

    for (j = 0; j < words.length;) {
      var w = words.slice(j, j += 16);
      var oldHash = h;
      h = h.slice(0, 8);

      for (i = 0; i < 64; i++) {
        var w15 = w[i - 15], w2 = w[i - 2];
        var a = h[0], e = h[4];
        var temp1 = h[7] +
          (rrot(6, e) ^ rrot(11, e) ^ rrot(25, e)) +
          ((e & h[5]) ^ (~e & h[6])) +
          k[i] +
          (w[i] = i < 16 ? w[i] : (
            w[i - 16] +
            (rrot(7, w15) ^ rrot(18, w15) ^ (w15 >>> 3)) +
            w[i - 7] +
            (rrot(17, w2) ^ rrot(19, w2) ^ (w2 >>> 10))
          ) | 0);
        var temp2 = (rrot(2, a) ^ rrot(13, a) ^ rrot(22, a)) +
          ((a & h[1]) ^ (a & h[2]) ^ (h[1] & h[2]));
        h = [(temp1 + temp2) | 0].concat(h);
        h[4] = (h[4] + temp1) | 0;
      }

      for (i = 0; i < 8; i++) h[i] = (h[i] + oldHash[i]) | 0;
    }

    for (i = 0; i < 8; i++) {
      for (j = 3; j + 1; j--) {
        var b = (h[i] >> (j * 8)) & 255;
        result += ((b < 16) ? 0 : '') + b.toString(16);
      }
    }
    return result;
  }

  function fireLead(eventAction, rawEmail, meta) {
    if (!eventAction) { log('no action, skipping'); return; }
    meta = meta || {};

    var email = normalizeEmail(rawEmail);
    var payload = {
      event: EVENT_NAME,
      eventModel: {
        event_category: 'form-submit',
        event_action: eventAction
      },
      user_data: {}
    };
    if (meta.formId) payload.form_id = String(meta.formId);
    if (meta.formName) payload.form_name = meta.formName;
    if (meta.submissionId) payload.submission_id = String(meta.submissionId);

    function push() {
      log('push', payload);
      window.dataLayer.push(payload);
    }

    if (!email) { push(); return; }

    payload.user_data.email = email;
    sha256Hex(email)
      .then(function (hash) { if (hash) payload.user_data.sha256_email = hash; push(); })
      .catch(function () { push(); });
  }

  // Public helper for non-Globo forms (Design Concierge questionnaire, etc.).
  window.LuminaireLeadEvents = {
    fire: function (eventAction, email, meta) { fireLead(eventAction, email, meta); }
  };

  // --- Globo success listener ------------------------------------------------

  document.addEventListener('globo.formbuilder.form.submitted', function (event) {
    var detail = (event && event.detail) || {};
    var form = detail.form;
    var formId = detail.formId || (form && form.getAttribute('data-id')) || '';

    var formName = getGloboFormName(formId, form);
    var eventAction = resolveGloboAction(formId, formName);

    fireLead(eventAction, getGloboEmail(form), {
      formId: formId,
      formName: formName,
      submissionId: getSubmissionId(detail)
    });
  });
})();
